'use client';

import * as React from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '@/lib/utils';
import { FIELD_CHROME, type FieldVariant } from '@/lib/field-chrome';
import { useFieldVariant } from '@/components/ui/field-variant';

interface MultiSelectProps {
  placeholder?: string;
  options: { label: string; value: string; searchValue?: string }[];
  selected: string[];
  onChange: (value: string[]) => void;
  className?: string;
  /** Visual variant. Defaults to the nearest `FieldVariantProvider`, or `'solid'`. */
  variant?: FieldVariant;
  /**
   * If set, caps the selection at N values. When additional items are
   * selected, the most recent N values are kept (older ones are dropped).
   */
  maxSelected?: number;
  /** When false, users can only pick from `options` (no free-text create). Default true. */
  creatable?: boolean;
  /** Optional callback for handling async search */
  onSearch?: (value: string) => void;
  /** Up and down arrows inside the field so it reads as a member picker. */
  showScrollArrows?: boolean;
}

const MULTI_SELECT_VARIANT = {
  solid: `${FIELD_CHROME.solid.base} ${FIELD_CHROME.solid.focusWithin}`,
  glass: `${FIELD_CHROME.glass.base} ${FIELD_CHROME.glass.focusWithin}`,
} as const;

export function MultiSelect({
  placeholder = 'Select options...',
  options,
  selected,
  onChange,
  className,
  variant,
  maxSelected,
  creatable = true,
  onSearch,
  showScrollArrows = false,
}: MultiSelectProps) {
  const resolvedVariant = useFieldVariant(variant);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const emit = React.useCallback(
    (next: string[]) => {
      const value = maxSelected && next.length > maxSelected ? next.slice(-maxSelected) : next;
      onChange(value);
      if (maxSelected && value.length >= maxSelected) {
        setOpen(false);
        inputRef.current?.blur();
      }
    },
    [maxSelected, onChange]
  );

  const handleUnselect = (value: string) => {
    emit(selected.filter((s) => s !== value));
  };

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (input.value === '' && selected.length > 0) {
            emit(selected.slice(0, -1));
          }
        }

        // Create new option on Comma
        if (creatable && e.key === ',') {
          e.preventDefault();
          if (inputValue.trim().length > 0) {
            const newVal = inputValue.trim();
            if (!selected.includes(newVal)) {
              emit([...selected, newVal]);
            }
            setInputValue('');
          }
        }

        if (e.key === 'Escape') {
          input.blur();
        }
      }
    },
    [inputValue, selected, emit, creatable]
  );

  const selectables = options.filter((option) => !selected.includes(option.value));

  const scrollOptions = (direction: 'up' | 'down') => {
    setOpen(true);
    inputRef.current?.focus();
    requestAnimationFrame(() => {
      listRef.current?.scrollBy({
        top: direction === 'down' ? 48 : -48,
        behavior: 'smooth',
      });
    });
  };

  return (
    <Command onKeyDown={handleKeyDown} className="overflow-visible bg-transparent">
      <div
        className={cn(
          'group flex min-h-11 w-full flex-wrap items-center gap-1 rounded-xl px-4 py-2 text-sm text-foreground ring-offset-background dark:text-neutral-100',
          MULTI_SELECT_VARIANT[resolvedVariant],
          className
        )}
      >
        {selected.map((value) => {
          const option = options.find((o) => o.value === value);
          return (
            <Badge
              key={value}
              variant="secondary"
              className="cursor-pointer hover:bg-destructive/10 hover:text-destructive transition-colors"
              onClick={() => handleUnselect(value)}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {option ? option.label : value}
              <button
                className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnselect(value);
                }}
                type="button"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            </Badge>
          );
        })}
        {/* Input for searching and creating */}
        <CommandPrimitive.Input
          ref={inputRef}
          value={inputValue}
          onValueChange={(val) => {
            setInputValue(val);
            if (onSearch) onSearch(val);
          }}
          onBlur={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="min-w-[120px] flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
        {showScrollArrows ? (
          <div className="ml-1 flex shrink-0 flex-col text-muted-foreground">
            <button
              type="button"
              aria-label="Scroll members up"
              className="flex h-4 w-4 items-center justify-center rounded-sm hover:text-foreground"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => scrollOptions('up')}
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Scroll members down"
              className="flex h-4 w-4 items-center justify-center rounded-sm hover:text-foreground"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => scrollOptions('down')}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
      <div className="relative mt-2">
        {open && (showScrollArrows || selectables.length > 0 || inputValue.trim().length > 0) && (
          <div
            className="absolute top-0 z-50 w-full rounded-xl border-2 border-border/80 bg-popover text-popover-foreground shadow-md outline-none animate-in"
            onMouseDown={(e) => e.preventDefault()}
          >
            <CommandList ref={listRef}>
              {!creatable && <CommandEmpty>No results found.</CommandEmpty>}
              <CommandGroup className="h-full overflow-auto">
                {selectables.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.searchValue || option.label}
                    onSelect={() => {
                      emit([...selected, option.value]);
                      setInputValue('');
                    }}
                    className={'cursor-pointer'}
                  >
                    {option.label}
                  </CommandItem>
                ))}
                {creatable &&
                  inputValue.length > 0 &&
                  !selectables.some((s) => s.label.toLowerCase() === inputValue.toLowerCase()) && (
                    <CommandItem
                      value={inputValue}
                      onSelect={() => {
                        const newVal = inputValue.trim();
                        if (!selected.includes(newVal)) {
                          emit([...selected, newVal]);
                        }
                        setInputValue('');
                      }}
                      className={'cursor-pointer'}
                    >
                      Create &quot;{inputValue}&quot;
                    </CommandItem>
                  )}
              </CommandGroup>
            </CommandList>
          </div>
        )}
      </div>
    </Command>
  );
}
